import React, { useEffect, useRef, useState } from "react";
import { Box, Flex, useDisclosure, useMediaQuery } from "@chakra-ui/react";
import { remCalc } from "theme/utils";
import { type Banner } from "lib/sanity";
import { IconButton } from "ui/button";
import { MenuIcon, LegacyCloseIcon } from "ui/icons";
import { useHeaderHeight } from "contexts/header-height";
import { useColorTheme } from "contexts/color-theme";
import { AnnouncementBanner } from "components/announcement-banner";
import { hideIntercom, showIntercom } from "hooks/use-intercom";
import { useScrollTransparency } from "hooks/use-scroll-transparency";
import { LogoPopover } from "./logo-popover";
import { DesktopNav } from "./desktop";
import { MobileNav } from "./mobile";
import { navTabs } from "./data";
import { CtaButtons } from "./cta-buttons";

export type HeaderProps = {
  announcementBanner?: Banner;
  isTransparent?: boolean;
};

export function Header({ announcementBanner, isTransparent }: HeaderProps) {
  const { isOpen, onToggle } = useDisclosure();
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const [isMobile] = useMediaQuery("(max-width: 992px)");
  const headerHeight = remCalc(92);
  const { setHeaderHeight } = useHeaderHeight();
  const [headerWidth, setHeaderWidth] = useState<number>();
  const { theme, bgColor } = useColorTheme();
  const headerElement = useRef<HTMLDivElement>(null);
  const scrolled = useScrollTransparency();

  const handlePopoverOpen = () => {
    setIsPopoverOpen(true);
  };

  const handlePopoverClose = () => {
    setIsPopoverOpen(false);
  };

  useEffect(() => {
    const headerObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setHeaderHeight(entry.contentRect.height);
        setHeaderWidth(entry.contentRect.width);
      }
    });

    if (headerElement.current) {
      headerObserver.observe(headerElement.current);
    }

    return () => {
      headerObserver.disconnect();
    };
  }, [setHeaderHeight]);

  useEffect(() => {
    if (isOpen && isMobile) {
      document.body.style.overflow = "hidden";
      hideIntercom();
    } else {
      document.body.style.removeProperty("overflow");
      showIntercom();
    }
  }, [isOpen, isMobile]);

  const isNowTransparent = isTransparent && !scrolled;
  const headerBg = !isNowTransparent || isOpen || isPopoverOpen ? bgColor : "whiteAlpha.100";

  return (
    <>
      {announcementBanner && !isOpen && <AnnouncementBanner {...announcementBanner} />}
      {isTransparent && <Box pos="fixed" top="0" left="0" right="0" bottom="0" height={headerHeight} bg={bgColor} />}
      <Box
        ref={headerElement}
        as="header"
        bg={headerBg}
        backdropFilter={isNowTransparent ? "blur(15px)" : "none"}
        color={`brand.text.${theme}`}
        height={headerHeight}
        layerStyle={`header.${isNowTransparent ? "transparent" : "base"}`}
        _hover={{ bg: { base: headerBg, lg: bgColor } }}
      >
        <Flex
          align="center"
          gap="smallest"
          height="100%"
          maxW="container.section"
          mx="auto"
          width="100%"
          px={{ base: "containerPx.mobile", xl: 0 }}
        >
          <LogoPopover />

          <DesktopNav
            tabs={navTabs}
            popoverWidth={headerWidth ? `${headerWidth}px` : "100vw"}
            onPopoverOpen={handlePopoverOpen}
            onPopoverClose={handlePopoverClose}
          />

          <CtaButtons display={{ base: "none", md: isOpen ? "none" : "flex", lg: "flex" }} />

          <IconButton
            aria-label={isOpen ? "Close navigation" : "Open navigation"}
            icon={isOpen ? LegacyCloseIcon : MenuIcon}
            display={{ base: "flex", lg: "none" }}
            ml="auto"
            onClick={onToggle}
          />
        </Flex>

        <MobileNav tabs={navTabs} isOpen={isOpen} onToggleNav={onToggle} />
      </Box>
    </>
  );
}
